import {
  AllocationValueTypeEnum,
  DEFAULT_SUBACCOUNT_ALLOCATION,
  convertSubAccountOrAllocationForUI,
  frontendAllocationsToBackendAllocations,
  parseDate,
  toBigWithDefault,
  type Allocation,
  type AllocationBE,
  type AssetTransaction,
  type AssetTransactionTypeEnum,
  type IAllocation,
  type SubAccount,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import type { AssetTransactionPatchFields, UpdateAssetTransactionRequest } from '../BalancesV2/assetTransactionsApi';
import { requiresPositionAsset } from '../BalancesV2/assetTransactionTypeOptions';
import { MAX_ASSET_TRANSACTION_COMMENT_LENGTH } from '../BalancesV2/useAdjustMarketAccountPositionsValidation';
import { isAssetTransactionTypeEditable } from './modifyAssetTransactionTypes';

export interface ModifyAssetTransactionForm {
  type: AssetTransactionTypeEnum;
  amount: string;
  asset: string;
  positionAsset: string;
  price: string;
  priceCurrency: string;
  rate: string;
  transactTime: Date | null;
  comment: string;
  allocationValueType: AllocationValueTypeEnum;
  subAccountAllocations: Allocation[];
  /** True after the user presses Clear Allocation until they pick a new allocation. */
  allocationCleared: boolean;
}

export type ModifyAssetTransactionFormErrors = Partial<Record<keyof ModifyAssetTransactionForm, string>>;

function optionalString(value: string | undefined | null): string {
  return value ?? '';
}

export function createModifyAssetTransactionForm(transaction: AssetTransaction): ModifyAssetTransactionForm {
  const { allocationValueType, subAccountAllocations } = convertSubAccountOrAllocationForUI(
    transaction.SubAccount,
    transaction.Allocation
  );

  return {
    type: transaction.Type,
    amount: optionalString(transaction.Amount),
    asset: optionalString(transaction.Asset),
    positionAsset: optionalString(transaction.PositionAsset),
    price: optionalString(transaction.Price),
    priceCurrency: optionalString(transaction.PriceCurrency),
    rate: optionalString(transaction.Rate),
    transactTime: transaction.TransactTime ? parseDate(transaction.TransactTime) : null,
    comment: optionalString(transaction.Comment),
    allocationValueType: allocationValueType ?? AllocationValueTypeEnum.Percentage,
    subAccountAllocations:
      subAccountAllocations?.length > 0 ? subAccountAllocations : [{ ...DEFAULT_SUBACCOUNT_ALLOCATION }],
    allocationCleared: false,
  };
}

function wasOriginallyAllocated(transaction: AssetTransaction): boolean {
  return Boolean(transaction.SubAccount) || Boolean(transaction.Allocation?.Allocations?.length);
}

function hasSelectedAllocation(form: ModifyAssetTransactionForm): boolean {
  return form.subAccountAllocations.some(allocation => allocation.subAccount.trim().length > 0);
}

function normalizeComment(comment: string): string {
  return comment.trim();
}

function buildAllocationPatch(
  form: ModifyAssetTransactionForm,
  subAccounts: SubAccount[] | undefined
): Pick<AssetTransactionPatchFields, 'Allocation' | 'SubAccount'> | undefined {
  if (form.allocationCleared && !hasSelectedAllocation(form)) {
    return { SubAccount: '' };
  }

  if (!hasSelectedAllocation(form)) {
    return undefined;
  }

  const filled = form.subAccountAllocations
    .filter(allocation => allocation.subAccount.trim().length > 0)
    .map(allocation => ({ ...allocation, subAccount: allocation.subAccount.trim() }));
  if (filled.length === 1) {
    return { SubAccount: filled[0].subAccount };
  }

  const allocation = frontendAllocationsToBackendAllocations({
    subAccountAllocations: filled,
    subAccounts,
    allocationValueType: form.allocationValueType,
    quantity: form.amount,
  });

  if (allocation == null) {
    return undefined;
  }

  return { Allocation: allocation as IAllocation };
}

function originalAllocationSnapshot(transaction: AssetTransaction): {
  SubAccount?: string;
  Allocation?: AllocationBE;
} {
  if (transaction.SubAccount) {
    return { SubAccount: transaction.SubAccount };
  }
  if (transaction.Allocation?.Allocations?.length) {
    return { Allocation: transaction.Allocation };
  }
  return {};
}

function allocationChanged(
  transaction: AssetTransaction,
  form: ModifyAssetTransactionForm,
  subAccounts: SubAccount[] | undefined
): boolean {
  if (form.allocationCleared && !hasSelectedAllocation(form)) {
    return wasOriginallyAllocated(transaction);
  }

  const next = buildAllocationPatch(form, subAccounts);
  if (next == null) {
    return false;
  }

  const original = originalAllocationSnapshot(transaction);
  if (next.SubAccount != null) {
    return next.SubAccount !== (original.SubAccount ?? '');
  }
  return !isEqual(next.Allocation, original.Allocation);
}

function rejectUnprovenClear(
  errors: ModifyAssetTransactionFormErrors,
  field: keyof ModifyAssetTransactionForm,
  nextValue: string,
  originalValue: string | undefined,
  label: string
) {
  if (!nextValue.trim() && optionalString(originalValue).trim()) {
    errors[field] = `${label} cannot be cleared`;
  }
}

function allocationTotalMustEqualPercentageMessage(): string {
  return 'Total must equal 100%';
}

function allocationTotalMustEqualQuantityMessage(amount: string, asset: string): string {
  const trimmedAmount = amount.trim();
  const trimmedAsset = asset.trim();

  if (trimmedAmount && trimmedAsset) {
    return `Total must equal ${trimmedAmount} ${trimmedAsset}`;
  }
  if (trimmedAmount) {
    return `Total must equal ${trimmedAmount}`;
  }

  return 'Total must equal the transaction amount';
}

function signOf(value: string): number {
  return toBigWithDefault(value, 0).cmp(0);
}

function validateFilledAllocations(form: ModifyAssetTransactionForm, filled: Allocation[]): string | undefined {
  if (filled.some(a => !a.value.trim())) {
    return 'Enter a value for each allocation';
  }

  if (form.allocationValueType === AllocationValueTypeEnum.Percentage) {
    if (filled.some(a => toBigWithDefault(a.value, 0).lte(0))) {
      return 'Each allocation must be greater than 0%';
    }
    const sum = filled.reduce(
      (working, allocation) => working.plus(toBigWithDefault(allocation.value, 0)),
      toBigWithDefault('0', 0)
    );
    if (sum.minus(100).abs().gt(1e-8)) {
      return allocationTotalMustEqualPercentageMessage();
    }
    return undefined;
  }

  const amount = form.amount.trim();
  const amountSign = signOf(amount);
  if (!amount || amountSign === 0) {
    return 'Amount must be non-zero for quantity allocations';
  }

  if (filled.some(a => signOf(a.value) === 0)) {
    return 'Each allocation must be non-zero';
  }

  if (filled.some(a => signOf(a.value) !== amountSign)) {
    return 'Each allocation must have the same sign as the transaction amount';
  }

  const sum = filled.reduce(
    (working, allocation) => working.plus(toBigWithDefault(allocation.value, 0)),
    toBigWithDefault('0', 0)
  );
  if (sum.minus(toBigWithDefault(amount, 0)).abs().gt(1e-8)) {
    return allocationTotalMustEqualQuantityMessage(form.amount, form.asset);
  }

  return undefined;
}

export function validateModifyAssetTransactionForm(
  form: ModifyAssetTransactionForm,
  transaction: AssetTransaction
): ModifyAssetTransactionFormErrors {
  const errors: ModifyAssetTransactionFormErrors = {};

  if (!form.amount.trim()) {
    errors.amount = 'Amount is required';
  }
  if (!form.asset.trim()) {
    errors.asset = 'Asset is required';
  }
  if (form.transactTime == null) {
    errors.transactTime = 'Transact Time is required';
  }
  if (form.comment.length > MAX_ASSET_TRANSACTION_COMMENT_LENGTH) {
    errors.comment = `Comment must be ${MAX_ASSET_TRANSACTION_COMMENT_LENGTH} characters or fewer`;
  }

  rejectUnprovenClear(errors, 'positionAsset', form.positionAsset, transaction.PositionAsset, 'Position Asset');
  if (requiresPositionAsset(form.type) && !form.positionAsset.trim()) {
    errors.positionAsset = 'Position Asset is required';
  }
  rejectUnprovenClear(errors, 'price', form.price, transaction.Price, 'Price');
  rejectUnprovenClear(errors, 'priceCurrency', form.priceCurrency, transaction.PriceCurrency, 'Price Currency');
  rejectUnprovenClear(errors, 'rate', form.rate, transaction.Rate, 'Rate');

  if (isAssetTransactionTypeEditable(transaction.Type) && !form.type) {
    errors.type = 'Type is required';
  }

  const allocationTouched =
    form.allocationCleared || hasSelectedAllocation(form) || wasOriginallyAllocated(transaction);

  if (allocationTouched && !form.allocationCleared) {
    if (!hasSelectedAllocation(form)) {
      // Unallocated original with empty selector is fine when not clearing / not allocating.
      if (wasOriginallyAllocated(transaction)) {
        errors.subAccountAllocations = 'Please select a Sub Account or clear the allocation';
      }
    } else {
      const filled = form.subAccountAllocations.filter(a => a.subAccount.trim().length > 0);
      const allocationError = validateFilledAllocations(form, filled);
      if (allocationError) {
        errors.subAccountAllocations = allocationError;
      }
    }
  }

  return errors;
}

/** Changed-only patch fields, independent of whether Revision can be sent. */
export function buildAssetTransactionPatchFields(
  transaction: AssetTransaction,
  form: ModifyAssetTransactionForm,
  subAccounts: SubAccount[] | undefined
): AssetTransactionPatchFields | undefined {
  const patch: AssetTransactionPatchFields = {};

  if (isAssetTransactionTypeEditable(transaction.Type) && form.type !== transaction.Type) {
    patch.Type = form.type;
  }

  if (form.amount.trim() !== optionalString(transaction.Amount)) {
    patch.Amount = form.amount.trim();
  }

  if (form.asset.trim() !== optionalString(transaction.Asset)) {
    patch.Asset = form.asset.trim();
  }

  if (form.positionAsset.trim() !== optionalString(transaction.PositionAsset)) {
    if (!form.positionAsset.trim()) {
      // Clearing PositionAsset is unproven on the backend — reject via validation instead.
    } else {
      patch.PositionAsset = form.positionAsset.trim();
    }
  }

  if (form.price.trim() !== optionalString(transaction.Price)) {
    if (form.price.trim()) {
      patch.Price = form.price.trim();
    }
  }

  if (form.priceCurrency.trim() !== optionalString(transaction.PriceCurrency)) {
    if (form.priceCurrency.trim()) {
      patch.PriceCurrency = form.priceCurrency.trim();
    }
  }

  if (form.rate.trim() !== optionalString(transaction.Rate)) {
    if (form.rate.trim()) {
      patch.Rate = form.rate.trim();
    }
  }

  const nextTransactTime = form.transactTime?.toISOString();
  const originalTransactTime = transaction.TransactTime
    ? (parseDate(transaction.TransactTime)?.toISOString() ?? transaction.TransactTime)
    : undefined;
  if (nextTransactTime && nextTransactTime !== originalTransactTime) {
    patch.TransactTime = nextTransactTime;
  }

  const nextComment = normalizeComment(form.comment);
  const originalComment = normalizeComment(optionalString(transaction.Comment));
  if (nextComment !== originalComment) {
    patch.Comment = nextComment;
  }

  if (allocationChanged(transaction, form, subAccounts)) {
    const allocationPatch = buildAllocationPatch(form, subAccounts);
    if (allocationPatch) {
      Object.assign(patch, allocationPatch);
    }
  }

  if (Object.keys(patch).length === 0) {
    return undefined;
  }

  return patch;
}

export function buildUpdateAssetTransactionRequest(
  transaction: AssetTransaction,
  form: ModifyAssetTransactionForm,
  subAccounts: SubAccount[] | undefined
): UpdateAssetTransactionRequest | undefined {
  // Revision 0 is valid for never-patched transactions (POST create returns 0).
  if (transaction.Revision == null) {
    return undefined;
  }

  const patch = buildAssetTransactionPatchFields(transaction, form, subAccounts);
  if (patch == null) {
    return undefined;
  }

  return {
    Revision: transaction.Revision,
    Patch: patch,
  };
}

export function isModifyAssetTransactionFormDirty(
  transaction: AssetTransaction,
  form: ModifyAssetTransactionForm,
  subAccounts: SubAccount[] | undefined
): boolean {
  return buildAssetTransactionPatchFields(transaction, form, subAccounts) != null;
}

/** Copied from Ava PATCH `/asset-transactions/{tid}` 409 body */
export function buildAssetTransactionRevisionConflictBody(requestRevision: number, currentRevision: number): string {
  return `The request is for revision ${requestRevision}, but current revision is ${currentRevision}`;
}

const ASSET_TRANSACTION_REVISION_CONFLICT_BODY_PATTERN =
  /^The request is for revision \d+, but current revision is \d+$/i;

export function isConflictUpdateError(errorMessage: string | undefined): boolean {
  if (!errorMessage) {
    return false;
  }
  return ASSET_TRANSACTION_REVISION_CONFLICT_BODY_PATTERN.test(errorMessage.trim());
}
